import { Controller } from "@hotwired/stimulus"
import iconRemove from "../../images/icon-mypage-modal-close.svg"

export default class extends Controller {
  static targets = ["modal", "overlay", "searchInput", "panel", "section", "sectionCheckbox", "skillCheckbox", "skillItem", "selectedSkillsContainer", "backCategoryName"]
  static values = { editUrl: String, multiple: { type: Boolean, default: true } }

  get _isSingleSelect() {
    return !this.multipleValue
  }

  switchTab(event) {
    const category = event.currentTarget.dataset.category

    // タブのアクティブ状態を切り替え
    this.element.querySelectorAll(".mypage-profile-section__skill-list-modal-category-tab").forEach(tab => {
      tab.classList.toggle("is-active", tab.dataset.category === category)
    })

    // パネルの表示を切り替え（inline style をリセットして is-active に戻す）
    this.panelTargets.forEach(panel => {
      panel.style.display = ""
      panel.classList.toggle("is-active", panel.dataset.category === category)
    })

    // タブ切替時に検索をリセット
    this.searchInputTargets.forEach(input => { input.value = "" })
    this._showAllSkillItems()
  }

  search(event) {
    const query = event.currentTarget.value.trim().toLowerCase()

    if (query === "") {
      this._showAllSkillItems()
      return
    }

    // SP: カテゴリ一覧を隠して検索結果パネルを表示
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("is-sp-search-view")
    }

    // セクション単位で表示/非表示を決定
    this.sectionTargets.forEach(section => {
      const middleCategory = (section.dataset.middleCategory || "").toLowerCase()
      const panel = section.closest("[data-large-category]")
      const largeCategory = panel ? (panel.dataset.largeCategory || "").toLowerCase() : ""

      // 大カテゴリまたは中カテゴリがヒット → セクション内の全スキルを表示
      if (largeCategory.includes(query) || middleCategory.includes(query)) {
        section.style.display = ""
        section.querySelectorAll("[data-skill-list-modal-target='skillItem']").forEach(item => {
          item.style.display = ""
        })
        return
      }

      // スキル名で個別フィルタ
      const items = section.querySelectorAll("[data-skill-list-modal-target='skillItem']")
      let hasVisible = false
      items.forEach(item => {
        const name = (item.dataset.skillName || "").toLowerCase()
        const visible = name.includes(query)
        item.style.display = visible ? "" : "none"
        if (visible) hasVisible = true
      })
      section.style.display = hasVisible ? "" : "none"
    })

    // パネル単位で inline style により表示/非表示を制御（is-active を上書き）
    const visiblePanels = []
    this.panelTargets.forEach(panel => {
      const sections = panel.querySelectorAll("[data-skill-list-modal-target='section']")
      const hasVisible = Array.from(sections).some(s => s.style.display !== "none")
      panel.style.display = hasVisible ? "block" : "none"
      if (hasVisible) visiblePanels.push(panel)
    })

    // 左タブのアクティブ状態を同期（複数パネルがヒットした場合は全て解除）
    const singleMatch = visiblePanels.length === 1
    this.element.querySelectorAll(".mypage-profile-section__skill-list-modal-category-tab").forEach(tab => {
      const isActive = singleMatch && visiblePanels[0].dataset.category === tab.dataset.category
      tab.classList.toggle("is-active", isActive)
    })
  }

  clearSearch() {
    this.searchInputTargets.forEach(input => { input.value = "" })
    this._showAllSkillItems()
  }

  clearAll() {
    this.skillCheckboxTargets.forEach(cb => { cb.checked = false })
    this.sectionCheckboxTargets.forEach(cb => { cb.checked = false })
    this.searchInputTargets.forEach(input => { input.value = "" })
    this._showAllSkillItems()
  }

  toggleSection(event) {
    if (this._isSingleSelect) return

    const sectionCheckbox = event.currentTarget
    const sectionKey = sectionCheckbox.id.replace("skill-section-", "")
    const isChecked = sectionCheckbox.checked

    this.skillCheckboxTargets.forEach(cb => {
      if (cb.dataset.section !== sectionKey) return
      const item = cb.closest("[data-skill-list-modal-target='skillItem']")
      if (item && item.style.display === "none") return
      cb.checked = isChecked
    })
  }

  openModal(event) {
    event.preventDefault()
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("is-open")
    }
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("is-open")
    }
    document.body.style.overflow = "hidden"
  }

  openModalForRow(event) {
    this._currentRowIndex = event.currentTarget.dataset.rowIndex

    // 現在の行のスキルIDを取得してプリセレクト
    const row = event.currentTarget.closest("[data-skill-row-index]")
    if (row) {
      const skillIdInput = row.querySelector("input[name*='[skill_id]']")
      const currentSkillId = skillIdInput ? skillIdInput.value : null

      this.skillCheckboxTargets.forEach(cb => {
        cb.checked = currentSkillId && cb.value === currentSkillId
      })
    }

    this.openModal(event)
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("is-open")
      this.modalTarget.classList.remove("is-sp-skills-view")
      this.modalTarget.classList.remove("is-sp-search-view")
    }
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("is-open")
    }
    document.body.style.overflow = ""
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.closeModal()
    }
  }

  // SP: 大カテゴリ行をタップしてScreen 2へ遷移
  showSpCategory(event) {
    const category = event.currentTarget.dataset.category

    // 対応するパネルをアクティブに
    this.panelTargets.forEach(panel => {
      panel.style.display = ""
      panel.classList.toggle("is-active", panel.dataset.category === category)
    })

    // 検索リセット
    this.searchInputTargets.forEach(input => { input.value = "" })
    this._showAllSkillItems()

    // バックヘッダーのカテゴリ名を更新
    if (this.hasBackCategoryNameTarget) {
      this.backCategoryNameTarget.textContent = category
    }

    // Screen 2へ切り替え
    this.modalTarget.classList.add("is-sp-skills-view")
  }

  // SP: Screen 2からScreen 1へ戻る
  goBack() {
    this.modalTarget.classList.remove("is-sp-skills-view")
    this.searchInputTargets.forEach(input => { input.value = "" })
    this._showAllSkillItems()
  }

  apply() {
    if (this._isSingleSelect && this._currentRowIndex != null) {
      this._applySingleToRow()
    } else {
      this._applyMultiple()
    }
  }

  _applyMultiple() {
    const selectedSkills = this._collectSelectedSkills()

    if (this.hasSelectedSkillsContainerTarget) {
      this._injectSkillTags(selectedSkills)
      this.closeModal()
    } else if (this.hasEditUrlValue) {
      const frame = document.querySelector("#new_member_skill_card")
      if (frame) {
        frame.addEventListener("turbo:frame-load", () => {
          this._injectSkillTags(selectedSkills)
        }, { once: true })
        frame.src = this.editUrlValue
      }
      this.closeModal()
    } else {
      this.closeModal()
    }
  }

  _applySingleToRow() {
    const selected = this.skillCheckboxTargets.find(cb => cb.checked)
    if (!selected) {
      this.closeModal()
      return
    }

    const skillItem = selected.closest("[data-skill-list-modal-target='skillItem']")
    const skillName = skillItem ? skillItem.dataset.skillName : ""
    const skillId = selected.value
    const rowIndex = this._currentRowIndex

    // 対象行を見つけてhidden inputsと表示名を更新
    const row = this.element.querySelector(`[data-skill-row-index="${rowIndex}"]`)
    if (row) {
      const skillIdInput = row.querySelector("input[name*='[skill_id]']")
      const nameInput = row.querySelector("input[name*='[name]']")
      const displaySpan = row.querySelector(".mypage-auto-fill-confirmation__after-value")

      if (skillIdInput) skillIdInput.value = skillId
      if (nameInput) nameInput.value = skillName
      if (displaySpan) displaySpan.textContent = skillName
    }

    this._currentRowIndex = null
    this.closeModal()
  }

  _collectSelectedSkills() {
    return this.skillCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => {
        const skillItem = cb.closest("[data-skill-list-modal-target='skillItem']")
        const skillName = skillItem ? skillItem.dataset.skillName : ""
        const section = cb.closest("[data-skill-list-modal-target='section']")
        const middleCat = section ? section.dataset.middleCategory : ""
        const panel = cb.closest("[data-large-category]")
        const largeCat = panel ? panel.dataset.largeCategory : ""
        return {
          id: cb.value,
          name: [largeCat, middleCat, skillName].filter(Boolean).join(" / ")
        }
      })
  }

  _injectSkillTags(selectedSkills) {
    if (!this.hasSelectedSkillsContainerTarget) return
    const container = this.selectedSkillsContainerTarget
    container.innerHTML = ""
    selectedSkills.forEach(({ id, name }) => {
      const tag = document.createElement("div")
      tag.className = "mypage-profile-section__skill-tag"
      tag.dataset.skillId = id

      // スキル名はユーザーが自由入力できるため、innerHTML には流さない
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "skill_ids[]"
      input.value = id

      const label = document.createElement("span")
      label.className = "mypage-profile-section__skill-tag-name"
      label.textContent = name

      const button = document.createElement("button")
      button.type = "button"
      button.className = "mypage-profile-section__skill-tag-remove"
      button.dataset.action = "click->skill-list-modal#removeSkill"
      button.dataset.skillId = id
      button.setAttribute("aria-label", "削除")

      const icon = document.createElement("img")
      icon.src = iconRemove
      icon.alt = "削除"
      icon.width = 10
      icon.height = 10

      button.appendChild(icon)
      tag.append(input, label, button)
      container.appendChild(tag)
    })
  }

  removeSkill(event) {
    const skillId = event.currentTarget.dataset.skillId
    const tag = event.currentTarget.closest(".mypage-profile-section__skill-tag")
    if (tag) tag.remove()

    this.skillCheckboxTargets.forEach(cb => {
      if (cb.value !== skillId) return
      cb.checked = false
      const sectionKey = cb.dataset.section
      const sectionCb = this.sectionCheckboxTargets.find(s => s.id === `skill-section-${sectionKey}`)
      if (sectionCb) sectionCb.checked = false
    })
  }

  _showAllSkillItems() {
    this.skillItemTargets.forEach(item => { item.style.display = "" })
    this.sectionTargets.forEach(section => { section.style.display = "" })
    // パネルの inline style をリセットして is-active による制御に戻す
    this.panelTargets.forEach(panel => { panel.style.display = "" })
    // タブのアクティブ状態を is-active クラスに基づいて復元
    this.panelTargets.forEach(panel => {
      const category = panel.dataset.category
      const tab = this.element.querySelector(`.mypage-profile-section__skill-list-modal-category-tab[data-category="${category}"]`)
      if (tab) tab.classList.toggle("is-active", panel.classList.contains("is-active"))
    })
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("is-sp-search-view")
    }
  }
}
